import { z } from "zod";

export const uuid = z.uuid();
export const trimmed = (max: number) => z.string().trim().max(max);
export const money = z.number().finite().min(0).max(1_000_000_000);
export const hexColor = z.string().regex(/^#[0-9A-Fa-f]{6}$/);
/** ISO-8601 string in, Date out. Accepts `null` where a field is optional. */
export const isoDate = z.iso.datetime({ offset: true }).transform((s) => new Date(s));
export const isoDateNullable = isoDate.nullable();

/**
 * PATCH schema derived from a create schema. Zod 4's `.partial()` keeps
 * `.default()`s and fills missing keys with them — which would silently reset
 * every field the client didn't send. Strip defaults first so an absent key
 * stays absent.
 */
export function partialOf<T extends z.ZodRawShape>(schema: z.ZodObject<T>) {
  const shape: Record<string, z.ZodType> = {};
  for (const [k, v] of Object.entries(schema.shape)) {
    shape[k] = v instanceof z.ZodDefault ? (v.removeDefault() as z.ZodType) : (v as z.ZodType);
  }
  return z.object(shape).partial() as unknown as z.ZodType<Partial<z.infer<z.ZodObject<T>>>>;
}

export const paginationQuery = z.object({
  limit: z.coerce.number().int().min(1).max(500).default(200),
  cursor: z.string().optional(),
});

/** Standard error envelope returned by the API for every non-2xx response. */
export const apiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
  }),
});
export type ApiError = z.infer<typeof apiErrorSchema>;

export const ERROR_CODES = [
  "VALIDATION_FAILED",
  "UNAUTHENTICATED",
  "FORBIDDEN",
  "NOT_FOUND",
  "CONFLICT",
  "FREE_LIMIT_REACHED",
  "INVITE_INVALID",
  "RATE_LIMITED",
  "PAYLOAD_TOO_LARGE",
  /** A paid tier's ceiling (active weddings, storage, velocity, templates). details: { feature, limit, plan }. */
  "PLAN_LIMIT_REACHED",
  /** The feature needs a higher tier. details: { required }. */
  "PLAN_REQUIRED",
  "BILLING_UNAVAILABLE",
  /** Contact form: the signed nonce was too young or too old; details: { retry: "too-fast" | "expired" }. The page resends by itself. */
  "CONTACT_RETRY",
  /** Contact form: the Turnstile human check was missing or failed. */
  "HUMAN_CHECK_FAILED",
  /** Guest RSVP page: replies are closed (deadline passed, wedding archived or replies switched off). */
  "RSVP_CLOSED",
  /** Shared run sheet: this link needs a passcode, or the one sent is wrong. */
  "SHARE_PASSCODE",
  "INTERNAL",
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];
